import { createAsyncThunk } from '@reduxjs/toolkit';
import getApiClient from 'js/apiClient';
import {
    IAdminElection,
    ICandidateForm,
    ICandidateRegistration,
    IElection,
    IVoteForm,
    RegistrationStatus,
} from './types';

export const asyncCreateCandidate = createAsyncThunk<string, ICandidateForm>(
    'elections/createCandidate',
    async (form, { rejectWithValue }) => {
        const formData = new FormData();
        Object.entries(form).forEach(([key, value]) => {
            if (value !== undefined && value !== '')
                formData.append(key, value);
        });
        return getApiClient('user')
            .post('elections/candidates/', formData)
            .then(() => {
                return 'ok';
            })
            .catch(error => {
                return rejectWithValue(error.response?.data || error.message);
            });
    }
);

export const asyncFetchMyCandidate = createAsyncThunk<
    ICandidateRegistration,
    void
>('elections/fetchMyCandidate', async (_, { rejectWithValue }) =>
    getApiClient('user')
        .get('elections/my_candidate/')
        .then(response => response.data)
        .catch(error => rejectWithValue(error.response?.data || error.message))
);

export const asyncUpdateCandidate = createAsyncThunk<string, ICandidateForm>(
    'elections/updateCandidate',
    async (form, { rejectWithValue }) => {
        const formData = new FormData();
        Object.entries(form).forEach(([key, value]) => {
            if (value !== undefined) formData.append(key, value);
        });
        return getApiClient('user')
            .patch('elections/my_candidate/', formData)
            .then(() => 'ok')
            .catch(error =>
                rejectWithValue(error.response?.data || error.message)
            );
    }
);

export const asyncFetchRegistrationStatus = createAsyncThunk<
    RegistrationStatus,
    void
>('elections/fetchRegistrationStatus', async (_, { rejectWithValue }) => {
    return getApiClient('user')
        .get('elections/registration_status/')
        .then(response => {
            return response.data.status ? 'submitted' : 'available';
        })
        .catch(error => {
            if (error.response?.status === 403) return 'forbidden';
            if (error.response?.status === 404) return 'closed';
            return rejectWithValue(error.response?.data || error.message);
        });
});

export const asyncFetchElections = createAsyncThunk<IElection[], void>(
    'elections/fetchElections',
    async (_, { rejectWithValue }) => {
        return getApiClient('user')
            .get('elections/')
            .then(response => {
                return response.data;
            })
            .catch(error => {
                throw rejectWithValue(error.message);
            });
    }
);

export const asyncFetchAdminElections = createAsyncThunk<
    IAdminElection[],
    void
>('elections/fetchAdminElections', async (_, { rejectWithValue }) => {
    return getApiClient('admin')
        .get('admin/elections/')
        .then(response => {
            return response.data;
        })
        .catch(error => {
            throw rejectWithValue(error.message);
        });
});

export const asyncFetchAdminElection = createAsyncThunk<IAdminElection, number>(
    'elections/fetchAdminElection',
    async (electionId, { rejectWithValue }) => {
        return getApiClient('admin')
            .get(`admin/elections/${electionId}/`)
            .then(response => {
                return response.data;
            })
            .catch(error => {
                throw rejectWithValue(error.message);
            });
    }
);

export const asyncFetchElection = createAsyncThunk<IElection, number>(
    'elections/fetchElection',
    async (id, { rejectWithValue }) => {
        return getApiClient('user')
            .get(`elections/${id}/`)
            .then(response => {
                return response.data;
            })
            .catch(error => {
                throw rejectWithValue(error.message);
            });
    }
);

export const asyncCreateVote = createAsyncThunk<string, IVoteForm>(
    'elections/createVote',
    async (form, { rejectWithValue }) => {
        const data = {
            candidate: form.candidate,
        };
        return getApiClient('user')
            .post(`elections/${form.election}/votes/`, data)
            .then(response => {
                return response.data;
            })
            .catch(error => {
                throw rejectWithValue(error.message);
            });
    }
);
